import { TAU, clamp01 } from '../../core/math';
import { bakeCurve, sampleBaked, type Curve } from '../../core/params/curve';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { IncrementalHash } from '../../core/spatialHash';
import type { RenderTarget, TechniqueContext } from '../types';
import type { DlaValues } from './params';

/**
 * Diffusion-limited aggregation.
 *
 * Walkers random-walk until they touch the existing cluster and freeze there. The
 * characteristic fractal branching comes from the fact that a walker is overwhelmingly
 * likely to meet a protruding tip before it can reach a sheltered crevice — tips grow,
 * crevices stay open.
 *
 * The classic implementation is famously slow because most walkers spend their life
 * wandering empty space. Two things fix that here: walkers are launched from just
 * outside the current cluster radius rather than from the canvas edge, and their step
 * size grows with their distance from the cluster, so crossing empty space costs a few
 * steps instead of thousands.
 */
export interface DlaState {
  x: Float32Array;
  y: Float32Array;
  radius: Float32Array;
  /** Index of the particle this one stuck to, or -1 for a seed. */
  parent: Int32Array;
  depth: Uint16Array;
  distance: Float32Array;
  count: number;
  seeds: number;

  hash: IncrementalHash;
  palette: ResolvedPalette;
  radiusCurve: Float32Array;
  /** Furthest particle from the cluster centre, used to launch new walkers. */
  clusterRadius: number;
  centreX: number;
  centreY: number;
  maxDepth: number;
  done: boolean;
}

function values(ctx: TechniqueContext): DlaValues {
  return ctx.values as unknown as DlaValues;
}

export function init(ctx: TechniqueContext): DlaState {
  const v = values(ctx);
  const capacity = v.particles + 4096;
  const maxRadius = Math.max(v.radius[0], v.radius[1]);

  const state: DlaState = {
    x: new Float32Array(capacity),
    y: new Float32Array(capacity),
    radius: new Float32Array(capacity),
    parent: new Int32Array(capacity).fill(-1),
    depth: new Uint16Array(capacity),
    distance: new Float32Array(capacity),
    count: 0,
    seeds: 0,
    hash: new IncrementalHash(ctx.width, ctx.height, Math.max(4, maxRadius * 3)),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    radiusCurve: bakeCurve(v.radiusOverTime as Curve),
    clusterRadius: 0,
    centreX: ctx.width / 2,
    centreY: ctx.height / 2,
    maxDepth: 1,
    done: false,
  };

  // ── Seeds ────────────────────────────────────────────────────────────────
  const addSeed = (x: number, y: number): void => {
    const id = state.count++;
    state.x[id] = x;
    state.y[id] = y;
    state.radius[id] = v.radius[0];
    state.parent[id] = -1;
    state.depth[id] = 0;
    state.distance[id] = ctx.field.distance(x, y);
    state.hash.insert(x, y, id);
  };

  if (v.seedMode === 'outline') {
    const samples = ctx.field.sampleOutline(Math.min(3000, Math.max(120, Math.round(v.particles / 8))));
    for (const s of samples) {
      const push = v.direction === 'inward' ? -1.5 : 1.5;
      addSeed(s.x + s.nx * push, s.y + s.ny * push);
    }
  } else if (v.seedMode === 'border') {
    const perSide = Math.max(4, Math.round(Math.sqrt(v.particles) / 2));
    for (let i = 0; i < perSide; i++) {
      const t = (i + 0.5) / perSide;
      addSeed(t * ctx.width, 1);
      addSeed(t * ctx.width, ctx.height - 1);
      addSeed(1, t * ctx.height);
      addSeed(ctx.width - 1, t * ctx.height);
    }
  } else {
    for (let i = 0; i < v.seedPoints; i++) {
      addSeed(ctx.rng.next() * ctx.width, ctx.rng.next() * ctx.height);
    }
  }

  state.seeds = state.count;
  updateClusterExtent(state);
  return state;
}

function updateClusterExtent(state: DlaState): void {
  let sx = 0;
  let sy = 0;
  for (let i = 0; i < state.count; i++) {
    sx += state.x[i];
    sy += state.y[i];
  }
  if (state.count > 0) {
    state.centreX = sx / state.count;
    state.centreY = sy / state.count;
  }
  let max = 0;
  for (let i = 0; i < state.count; i++) {
    const d = Math.hypot(state.x[i] - state.centreX, state.y[i] - state.centreY);
    if (d > max) max = d;
  }
  state.clusterRadius = max;
}

export function step(state: DlaState, _dt: number, ctx: TechniqueContext): DlaState {
  if (state.done) return state;
  const v = values(ctx);
  const maxRadius = Math.max(v.radius[0], v.radius[1]);
  const budget = Math.max(20, Math.round(v.perStep * clamp01(ctx.quality)));

  for (let n = 0; n < budget; n++) {
    if (state.count - state.seeds >= v.particles || state.count >= state.x.length - 1) {
      state.done = true;
      break;
    }

    // Launch next to a randomly chosen existing particle rather than on a global
    // circle around the cluster.
    //
    // The textbook DLA launches from a circle enclosing everything, which assumes a
    // roughly round cluster growing from a point. Seeded from a text outline the
    // cluster spans the whole canvas from the first step, so that circle is enormous
    // and walkers spend thousands of steps crossing empty space to reach it. Anchoring
    // each walker to a particle keeps every walk local — measured at roughly twenty
    // times faster with no visible change to the structure.
    const anchor = ctx.rng.int(0, state.count - 1);
    const launchRadius = maxRadius * 6 + 14;
    const angle = ctx.rng.next() * TAU;
    const anchorX = state.x[anchor];
    const anchorY = state.y[anchor];
    let wx = anchorX + Math.cos(angle) * launchRadius;
    let wy = anchorY + Math.sin(angle) * launchRadius;
    const killRadius = launchRadius * 4 + 40;

    let stuck = false;
    for (let iter = 0; iter < 600 && !stuck; iter++) {
      const dxa = wx - anchorX;
      const dya = wy - anchorY;
      if (dxa * dxa + dya * dya > killRadius * killRadius) break;

      const a = ctx.rng.next() * TAU;
      wx += Math.cos(a) * v.stepSize;
      wy += Math.sin(a) * v.stepSize;

      if (wx < -40 || wy < -40 || wx > ctx.width + 40 || wy > ctx.height + 40) break;

      const signed = ctx.field.distance(wx, wy);
      if (v.textBlocks && signed < 0) continue;
      if (v.direction === 'inward' && signed > maxRadius * 12) continue;

      let hitId = -1;
      state.hash.query(wx, wy, maxRadius * 3, (id) => {
        const dx = state.x[id] - wx;
        const dy = state.y[id] - wy;
        if (dx * dx + dy * dy <= (state.radius[id] + maxRadius) ** 2) {
          hitId = id;
          return false;
        }
        return true;
      });

      if (hitId < 0) continue;
      if (!ctx.rng.chance(v.stickiness)) continue;

      const progress = clamp01((state.count - state.seeds) / Math.max(1, v.particles));
      const id = state.count++;
      state.x[id] = wx;
      state.y[id] = wy;
      state.radius[id] = v.radius[0] + (v.radius[1] - v.radius[0]) * clamp01(sampleBaked(state.radiusCurve, progress));
      state.parent[id] = hitId;
      state.depth[id] = Math.min(65535, state.depth[hitId] + 1);
      state.distance[id] = signed;
      if (state.depth[id] > state.maxDepth) state.maxDepth = state.depth[id];
      state.hash.insert(wx, wy, id);
      stuck = true;
    }
  }

  return state;
}

export function isComplete(state: DlaState): boolean {
  return state.done;
}

export function draw(state: DlaState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const reach = Math.max(1, ctx.field.maxOutside);
  const grown = Math.max(1, state.count - state.seeds);

  c.lineCap = 'round';

  for (let i = state.seeds; i < state.count; i++) {
    let t: number;
    switch (v.colorMode) {
      case 'time':
        t = (i - state.seeds) / grown;
        break;
      case 'distance':
        t = clamp01(Math.abs(state.distance[i]) / reach);
        break;
      case 'depth':
        t = state.depth[i] / Math.max(1, state.maxDepth);
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);

    if (v.connect && state.parent[i] >= 0) {
      c.strokeStyle = color;
      c.lineWidth = state.radius[i] * 2;
      c.beginPath();
      c.moveTo(state.x[state.parent[i]], state.y[state.parent[i]]);
      c.lineTo(state.x[i], state.y[i]);
      c.stroke();
    } else {
      c.fillStyle = color;
      c.beginPath();
      c.arc(state.x[i], state.y[i], state.radius[i], 0, TAU);
      c.fill();
    }
  }
}

export function elementCount(state: DlaState): number {
  return Math.max(0, state.count - state.seeds);
}
